import { useEffect, useRef } from 'react'
import { noteTakerStore } from 'shared/stores/noteTakerStore'
import { getLatestAiJob, getSessionNote } from 'api/noteTakerSessionApi'
import { AI_JOB_STATUS } from 'widgets/NoteTaker/types/aiProcessing'

const INITIAL_POLL_MS = 2000
const MAX_POLL_MS = 5000

function wait(ms, signal) {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason || new DOMException('Aborted', 'AbortError'))
			return
		}

		const timeoutId = window.setTimeout(resolve, ms)
		const onAbort = () => {
			window.clearTimeout(timeoutId)
			reject(signal?.reason || new DOMException('Aborted', 'AbortError'))
		}

		signal.addEventListener('abort', onAbort, { once: true })
	})
}

export function useNoteTakerAiProcessing() {
	const pollDelayRef = useRef(INITIAL_POLL_MS)
	const isPollingRef = useRef(false)
	const shouldPollAiProcessing = noteTakerStore.shouldPollAiProcessing
	const sessionNoteId = noteTakerStore.sessionNoteId

	useEffect(() => {
		if (!shouldPollAiProcessing || !sessionNoteId) {
			pollDelayRef.current = INITIAL_POLL_MS
			return undefined
		}

		noteTakerStore.beginAiProcessingPoll()

		const abortController = new AbortController()
		noteTakerStore._aiPollAbortController = abortController
		let cancelled = false

		const poll = async () => {
			if (cancelled || abortController.signal.aborted) return
			if (!shouldPollAiProcessing) return
			if (isPollingRef.current) return

			isPollingRef.current = true

			try {
				const job = await getLatestAiJob(sessionNoteId, {
					signal: abortController.signal,
				})
				if (cancelled || abortController.signal.aborted) return

				if (job.status === AI_JOB_STATUS.COMPLETED) {
					// Finish before marking COMPLETED — otherwise shouldPollAiProcessing
					// flips false and this effect aborts the in-flight getSessionNote.
					const sessionNote = await getSessionNote(sessionNoteId, {
						signal: abortController.signal,
					})
					if (cancelled || abortController.signal.aborted) return
					await noteTakerStore.finishAiProcessing(sessionNote)
					return
				}

				noteTakerStore.applyAiJobPollResult(job)

				if (job.status === AI_JOB_STATUS.FAILED) {
					return
				}

				pollDelayRef.current = Math.min(
					Math.round(pollDelayRef.current * 1.25),
					MAX_POLL_MS,
				)
			} catch (cause) {
				if (cause?.name === 'AbortError') return

				try {
					const latestJob = await getLatestAiJob(sessionNoteId)
					if (latestJob?.status === AI_JOB_STATUS.COMPLETED) {
						await noteTakerStore.recoverCompletedAiProcessing(sessionNoteId)
						return
					}
				} catch {
					// Fall through to failed state below.
				}

				noteTakerStore.failAiProcessing({
					code: cause?.name || 'AiPollingError',
					message: cause?.message || 'Failed to check AI processing status',
				})
				return
			} finally {
				isPollingRef.current = false
			}

			if (cancelled || abortController.signal.aborted) return
			if (!noteTakerStore.shouldPollAiProcessing) return

			try {
				await wait(pollDelayRef.current, abortController.signal)
			} catch {
				return
			}

			poll()
		}

		poll()

		return () => {
			cancelled = true
			abortController.abort()
			if (noteTakerStore._aiPollAbortController === abortController) {
				noteTakerStore._aiPollAbortController = null
			}
		}
	}, [shouldPollAiProcessing, sessionNoteId])
}
